import React, { useState, useEffect } from 'react';
import { Button, Form, Card, Alert, Row, Col, InputGroup, Badge } from 'react-bootstrap';
import { Save, X, Plus, DollarSign, Tag, ShoppingBag, FileText, Layers, Upload, Image as ImageIcon, Trash, Barcode } from 'lucide-react';
import useForm from '../../hooks/useForm';
import { createProduct, getProductById, updateProduct, addProductImage, removeProductImage } from '../../api/products';
import { getAllCategories } from '../../api/categories';

const ProductForm = ({ productId, onCancel, onSuccess }) => {
    const isEditMode = !!productId;
    const [loading, setLoading] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [images, setImages] = useState([]);
    const [imageFiles, setImageFiles] = useState([]);
    const [mainImageIndex, setMainImageIndex] = useState(0);

    const initialValues = {
        name: '',
        description: '',
        dimensions: '',
        basePrice: '',
        prixFournisseur: '',
        sku: '',
        codeBarre: '',
        pcb: '',
        numeroProduit: 0,
        category: '',
    };

    const { values, setValues, handleChange, errors, validateForm } = useForm(initialValues);

    const [categories, setCategories] = useState([]);


    useEffect(() => {
        if (isEditMode) {
            fetchProductData();
        }
    }, [productId]);

    useEffect(() => {
        (async () => {
            try {
                setCategories(await getAllCategories());
            } catch (e) {
                console.error('Erreur chargement catégories:', e);
            }
        })();
    }, []);

    const fetchProductData = async () => {
        try {
            setLoading(true);
            const productData = await getProductById(productId);

            setValues({
                name: productData.name,
                description: productData.description || '',
                dimensions: productData.dimensions || '',
                basePrice: productData.basePrice.toString(),
                pcb: productData.pcb,
                prixFournisseur: productData.prixFournisseur != null ? productData.prixFournisseur.toString() : '',
                stockQuantity: productData.stockQuantity,
                sku: productData.sku,
                codeBarre: productData.codeBarre || '',
                numeroProduit: productData.numeroProduit || 0,
                category: productData.category?.name || '',
            });

            if (productData.images && productData.images.length > 0) {
                setImages(productData.images);
                const mainIndex = productData.images.findIndex(img => img.mainImage);
                if (mainIndex !== -1) {
                    setMainImageIndex(mainIndex);
                }
            }
        } catch (err) {
            setError('Failed to load product data. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

     const getFullImageUrl = (path) => {
        if (!path) return null;

        // Si déjà une URL complète
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }

        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        //const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://102.164.134.9';


        // Si déjà commence par "uploads/"
        if (path.startsWith('uploads/')) {
            return `${API_BASE_URL}/${path}`;
        }

        // Si commence par "products/"
        if (path.startsWith('products/')) {
            return `${API_BASE_URL}/uploads/${path}`;
        }

        // Si commence par un ID de produit (ex: "4/nom.jpg")
        if (/^\d+\//.test(path)) {
            return `${API_BASE_URL}/uploads/products/${path}`;
        }

        // Si juste le nom de fichier
        return `${API_BASE_URL}/uploads/products/${path}`;
    };


    const validationSchema = {
        name: {
            required: true,
            errorMessage: 'Le nom du produit est requis'
        },
        basePrice: {
            required: true,
            pattern: /^\d+(\.\d{1,2})?$/,
            errorMessage: 'Le prix de base est requis et doit être un nombre valide'
        },
        sku: {
            required: true,
            errorMessage: 'La référence du produit est requise'
        },
        numeroProduit: {
            pattern: /^\d*$/,
            errorMessage: 'Le numéro du produit doit être un nombre entier'
        },
        
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Validate form
        const isValid = validateForm(validationSchema);
        if (!isValid) {
            setLoading(false);
            return;
        }

        try {
            const productData = {
                ...values,
                categoryName: values.category,
                basePrice: parseFloat(values.basePrice),
                prixFournisseur: (values.prixFournisseur !== '' && values.prixFournisseur != null) ? parseFloat(values.prixFournisseur) : null,
                stockQuantity: parseInt(values.stockQuantity, 10)
            };
            delete productData.category; // le backend lit categoryName ; la relation category est en lecture seule

            if (isEditMode) {
                // Update existing product
                await updateProduct(productId, productData);
                setSuccess('Product updated lly!');
            } else {
                // Create new product
                await createProduct(productData, imageFiles, mainImageIndex);
                setSuccess('Product created successfully!');
            }

            // Call the success callback after a short delay to allow the user to see the success message
            setTimeout(() => {
                if (onSuccess) onSuccess();
            }, 1500);
        } catch (err) {
            setError(err.message || 'An error occurred. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);

        // Validate file types
        const validFiles = files.filter(file => {
            const fileType = file.type;
            return fileType === 'image/jpeg' || fileType === 'image/png' || fileType === 'image/webp';
        });

        if (validFiles.length !== files.length) {
            alert('Only JPEG, PNG, and WebP images are allowed');
        }

        // Create preview URLs
        const filePreviews = validFiles.map(file => ({
            url: URL.createObjectURL(file),
            filename: file.name,
            file: file
        }));

        setImageFiles([...imageFiles, ...validFiles]);
        setImages([...images, ...filePreviews.map((preview, index) => ({
            id: `new-${Date.now()}-${index}`,
            path: preview.url,
            mainImage: false,
            isNew: true,
            file: preview.file
        }))]);
    };

    const handleRemoveImage = async (index) => {
        const imageToRemove = images[index];

        if (isEditMode && !imageToRemove.isNew) {
            try {
                setImageUploading(true);
                setError('');

                console.log("Suppression de l'image:", imageToRemove.id, "du produit:", productId);
                await removeProductImage(productId, imageToRemove.id);
                console.log("Image supprimée avec succès");

                // Mettre à jour l'état local sans recharger toutes les données
                const newImages = [...images];
                newImages.splice(index, 1);
                setImages(newImages);

                // Mettre à jour l'index de l'image principale si nécessaire
                if (mainImageIndex === index) {
                    setMainImageIndex(newImages.length > 0 ? 0 : -1);
                } else if (mainImageIndex > index) {
                    setMainImageIndex(mainImageIndex - 1);
                }

                setSuccess('Image supprimée avec succès!');
            } catch (err) {
                console.error("Erreur détaillée lors de la suppression:", err);
                setError(`Impossible de supprimer l'image: ${err.message || 'Erreur inconnue'}`);
            } finally {
                setImageUploading(false);
            }
        } else {
            // Pour les nouvelles images non encore sauvegardées, juste mettre à jour l'état
            const newImages = [...images];
            newImages.splice(index, 1);
            setImages(newImages);

            // Aussi supprimer des imageFiles
            if (imageToRemove.isNew) {
                const newImageFiles = [...imageFiles];
                const fileIndex = newImageFiles.findIndex(file =>
                    file.name === imageToRemove.file.name && file.size === imageToRemove.file.size
                );
                if (fileIndex !== -1) {
                    newImageFiles.splice(fileIndex, 1);
                    setImageFiles(newImageFiles);
                }
            }

            // Mettre à jour l'index de l'image principale si nécessaire
            if (mainImageIndex === index) {
                setMainImageIndex(newImages.length > 0 ? 0 : -1);
            } else if (mainImageIndex > index) {
                setMainImageIndex(mainImageIndex - 1);
            }
        }
    };

    const handleUploadImage = async (e) => {
        e.preventDefault();
        const files = Array.from(e.target.files);

        if (!files.length || !isEditMode) return;

        try {
            setImageUploading(true);
            setError('');

            const file = files[0];
            const isMain = images.length === 0; // Si c'est la première image, la définir comme principale

            console.log("Envoi de l'image:", file.name, "taille:", file.size, "type:", file.type);

            const response = await addProductImage(productId, file, isMain);
            console.log("Réponse du serveur après ajout d'image:", response);

            // Au lieu d'appeler fetchProductData, mettre à jour manuellement l'état
            // Cela évite un rechargement complet qui peut échouer
            if (response && response.id) {
                const newImage = {
                    id: response.id,
                    path: response.path,
                    mainImage: isMain,
                    description: null
                };

                setImages(prevImages => [...prevImages, newImage]);

                if (isMain) {
                    setMainImageIndex(prevImages => prevImages.length);
                }
            } else {
                // Si la réponse n'est pas au bon format, rechargeons quand même les données
                await fetchProductData();
            }

            setSuccess('Image ajoutée avec succès!');
        } catch (err) {
            console.error("Erreur détaillée lors de l'upload:", err);
            setError(`Impossible d'ajouter l'image: ${err.message || 'Erreur inconnue'}`);
        } finally {
            setImageUploading(false);
            // Réinitialiser l'input file pour pouvoir sélectionner le même fichier à nouveau
            e.target.value = null;
        }
    };


    const setAsMainImage = (index) => {
        setMainImageIndex(index);

        // If in edit mode, update the mainImage property for all images
        if (isEditMode) {
            const updatedImages = images.map((img, i) => ({
                ...img,
                mainImage: i === index
            }));
            setImages(updatedImages);
        }
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <h5 className="mb-0">{isEditMode ? 'Modifier Produit' : 'Ajouter un nouveau produit'}</h5>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nom Produit</Form.Label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <ShoppingBag size={18} />
                                    </span>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        placeholder="Entrer nom produit"
                                        required
                                    />
                                </div>
                                {errors.name && <Form.Text className="text-danger">{errors.name}</Form.Text>}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Référence</Form.Label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Tag size={18} />
                                    </span>
                                    <Form.Control
                                        type="text"
                                        name="sku"
                                        value={values.sku}
                                        onChange={handleChange}
                                        placeholder="Entrer ref produit"
                                        required
                                    />
                                </div>
                                {errors.sku && <Form.Text className="text-danger">{errors.sku}</Form.Text>}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Prix de vente</Form.Label>
                                <InputGroup>
                                    
                                    <Form.Control
                                        type="text"
                                        name="basePrice"
                                        value={values.basePrice}
                                        onChange={handleChange}
                                        placeholder="Entrer le prix"
                                        required
                                    />
                                </InputGroup>
                                {errors.basePrice && <Form.Text className="text-danger">{errors.basePrice}</Form.Text>}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>PCB (pièces par paquet)</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Layers size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        name="pcb"
                                        value={values.pcb}
                                        onChange={handleChange}
                                        placeholder="Nombre de pièces par colis"
                                        min="0"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Catégorie</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Layers size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="category"
                                        value={values.category}
                                        onChange={handleChange}
                                        placeholder="Entrer ou choisir une catégorie"
                                        list="categories-suggestions"
                                        autoComplete="off"
                                    />
                                <datalist id="categories-suggestions">
                                    {categories.map((c) => (
                                        <option key={c.id} value={c.name} />
                                    ))}
                                </datalist>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Dimensions</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="dimensions"
                                    value={values.dimensions}
                                    onChange={handleChange}
                                    placeholder="Entrer les dimensions (e.g., 10x20x5 cm)"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Numéro du produit</Form.Label>
                                <Form.Control
                                    type="number"
                                    onWheel={(e) => e.target.blur()}
                                    name="numeroProduit"
                                    value={values.numeroProduit}
                                    onChange={handleChange}
                                    placeholder="Entrer le numéro du produit"
                                    min="0"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Code-barres</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Barcode size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="codeBarre"
                                        value={values.codeBarre}
                                        onChange={handleChange}
                                        placeholder="Scanner ou saisir le code-barres"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Prix fournisseur</Form.Label>
                                <InputGroup>
                                    
                                    <Form.Control
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        min="0"
                                        step="0.01"
                                        name="prixFournisseur"
                                        value={values.prixFournisseur}
                                        onChange={handleChange}
                                        placeholder="Prix d'achat fournisseur"
                                    />
                                </InputGroup>
                                <Form.Text className="text-muted">
                                    Utilisé par défaut dans les commandes fournisseurs.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-4">
                        <Form.Label>Description</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>
                                <FileText size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                as="textarea"
                                name="description"
                                value={values.description}
                                onChange={handleChange}
                                placeholder="Entrer description produit"
                                style={{ height: '100px' }}
                            />
                        </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label>Images Produits</Form.Label>

                        <div className="d-flex flex-wrap gap-3 mb-3">
                            {images.map((image, index) => (
                                <div key={index} className="position-relative" style={{ width: '120px' }}>
                                    <div
                                        className={`border rounded p-1 ${mainImageIndex === index ? 'border-primary' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setAsMainImage(index)}
                                    >
                                        <img
                                            src={image.isNew ? image.path : getFullImageUrl(image.path)}
                                            alt={`Product ${index + 1}`}
                                            className="img-fluid"
                                            style={{ height: '100px', objectFit: 'cover', width: '100%' }}
                                            onError={(e) => {
                                                console.error("Error loading image:", e);
                                                e.target.onerror = null;
                                                e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>`;
                                            }}
                                        />
                                        {mainImageIndex === index && (
                                            <Badge className="position-absolute top-0 start-0 m-1 bg-primary">Main</Badge>
                                        )}
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute top-0 end-0 m-1 p-0"
                                            style={{ width: '20px', height: '20px' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveImage(index);
                                            }}
                                        >
                                            <X size={12} />
                                        </Button>
                                    </div>
                                </div>
                            ))}

                            {/* Add image button */}
                            <div className="border rounded p-1 d-flex justify-content-center align-items-center"
                                style={{ width: '120px', height: '120px', cursor: 'pointer' }}>
                                <label htmlFor={isEditMode ? "edit-product-image" : "product-image"} style={{ cursor: 'pointer' }}>
                                    <div className="text-center">
                                        <Plus size={24} className="mb-2 text-primary" />
                                        <div className="small text-muted">Ajouter Image</div>
                                    </div>
                                    <input
                                        type="file"
                                        id={isEditMode ? "edit-product-image" : "product-image"}
                                        accept="image/jpeg,image/png,image/webp"
                                        style={{ display: 'none' }}
                                        onChange={isEditMode ? handleUploadImage : handleImageChange}
                                        multiple={!isEditMode}
                                    />
                                </label>
                            </div>
                        </div>

                        <Form.Text className="text-muted">
                        Téléchargez des images du produit. La première image ou l’image sélectionnée sera utilisée comme image principale du produit.
                        </Form.Text>
                    </Form.Group>

                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            variant="outline-secondary"
                            onClick={onCancel}
                            disabled={loading || imageUploading}
                        >
                            <X size={16} className="me-1" />
                            Annuler
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={loading || imageUploading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {isEditMode ? 'Modication...' : 'Créer...'}
                                </>
                            ) : (
                                <>
                                    <Save size={16} className="me-1" />
                                    {isEditMode ? 'Modifier produit' : 'Créer Produit'}
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default ProductForm;